/**
 * PeptideForge — Offline Mutation Queue
 *
 * Provides:
 *   • enqueueMutation(type, payload)  — store an action while offline
 *   • flush(apiBase)                  — drain the queue against /api/sync/push (idempotent)
 *   • pullChanges(apiBase, since)     — pull server-side changes since timestamp
 *   • Online/offline event wiring for auto-flush
 *
 * Backed by localforage (IndexedDB) so it survives page refreshes and app restarts.
 */
import localforage from 'localforage';
import { API } from '@/config/api';

const queueStore = localforage.createInstance({
  name: 'peptideforge',
  storeName: 'sync_queue_v2',
});

const metaStore = localforage.createInstance({
  name: 'peptideforge',
  storeName: 'sync_meta_v2',
});

const LAST_PULL_KEY = 'last_pull_at';
const MAX_BATCH = 100;
const MAX_ATTEMPTS = 5;

// In-memory subscribers for sync state changes
const listeners = new Set();
let flushInFlight = false;

const notify = (payload) => {
  for (const fn of listeners) {
    try { fn(payload); } catch (_) { /* no-op */ }
  }
};

const uuid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `mut_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

/**
 * Public API
 */

export const subscribeSync = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const isOnline = () =>
  typeof navigator === 'undefined' ? true : navigator.onLine !== false;

export const getPendingMutations = async () => {
  const out = [];
  await queueStore.iterate((value) => { if (value) out.push(value); });
  return out.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
};

export const getPendingCount = async () => {
  let n = 0;
  await queueStore.iterate(() => { n += 1; });
  return n;
};

export const enqueueMutation = async (type, payload) => {
  const mut = {
    mutation_id: uuid(),
    type,
    payload: payload || {},
    client_created_at: new Date().toISOString(),
    created_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
  };
  await queueStore.setItem(mut.mutation_id, mut);
  notify({ kind: 'enqueued', mutation: mut });

  // Try an immediate flush if we're online (best-effort, fire-and-forget)
  if (isOnline()) {
    flush().catch(() => { /* swallow — next online event will retry */ });
  }
  return mut;
};

export const clearQueue = async () => {
  await queueStore.clear();
  notify({ kind: 'cleared' });
};

const getLastPullAt = async () => (await metaStore.getItem(LAST_PULL_KEY)) || null;
const setLastPullAt = async (iso) => { await metaStore.setItem(LAST_PULL_KEY, iso); };

export { getLastPullAt };

/**
 * Drain queued mutations to the backend.
 * Returns {applied, skipped, errors, pendingRemaining}
 */
export const flush = async () => {
  if (flushInFlight) return { applied: 0, skipped: 0, errors: [], pendingRemaining: await getPendingCount() };
  if (!isOnline()) {
    notify({ kind: 'flush_skipped_offline' });
    return { applied: 0, skipped: 0, errors: [], pendingRemaining: await getPendingCount(), offline: true };
  }

  flushInFlight = true;
  notify({ kind: 'flush_started' });

  let appliedTotal = 0;
  let skippedTotal = 0;
  let errorsTotal = [];

  try {
    while (true) {
      const all = await getPendingMutations();
      if (all.length === 0) break;

      const batch = all.slice(0, MAX_BATCH);
      const body = {
        mutations: batch.map(({ mutation_id, type, payload, client_created_at }) => ({
          mutation_id, type, payload, client_created_at,
        })),
      };

      let response;
      try {
        response = await fetch(`${API}/sync/push`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        });
      } catch (netErr) {
        // Network died mid-flush → bump attempts and abort
        for (const m of batch) {
          await queueStore.setItem(m.mutation_id, {
            ...m,
            attempts: (m.attempts || 0) + 1,
            last_error: 'network_error',
          });
        }
        errorsTotal.push({ kind: 'network', error: String(netErr) });
        break;
      }

      if (response.status === 401) {
        notify({ kind: 'flush_unauthorized' });
        errorsTotal.push({ kind: 'auth', error: 'unauthorized' });
        break;
      }

      if (!response.ok) {
        // Bump attempts; pause flush so we don't hot-loop
        for (const m of batch) {
          const attempts = (m.attempts || 0) + 1;
          if (attempts >= MAX_ATTEMPTS) {
            await queueStore.removeItem(m.mutation_id);
            errorsTotal.push({ mutation_id: m.mutation_id, error: `dropped_after_${MAX_ATTEMPTS}_attempts` });
          } else {
            await queueStore.setItem(m.mutation_id, {
              ...m,
              attempts,
              last_error: `http_${response.status}`,
            });
          }
        }
        break;
      }

      const data = await response.json();
      const results = data.results || [];

      // Remove anything the server applied or marked duplicate
      for (const r of results) {
        const status = r.status || '';
        if (status === 'applied' || status.startsWith('skipped') || status === 'not_found') {
          await queueStore.removeItem(r.mutation_id);
        } else {
          // status === 'error' → bump attempts, drop on max
          const m = batch.find((x) => x.mutation_id === r.mutation_id);
          if (m) {
            const attempts = (m.attempts || 0) + 1;
            if (attempts >= MAX_ATTEMPTS) {
              await queueStore.removeItem(m.mutation_id);
              errorsTotal.push({ mutation_id: m.mutation_id, error: r.error || 'unknown', dropped: true });
            } else {
              await queueStore.setItem(m.mutation_id, { ...m, attempts, last_error: r.error });
            }
          }
        }
      }

      appliedTotal += data.applied || 0;
      skippedTotal += data.skipped || 0;
      if (data.errors && data.errors.length) errorsTotal = errorsTotal.concat(data.errors);

      // Safety: if nothing was removed (server returned all errors and we're not dropping), break
      const remainingAfter = await getPendingCount();
      if (remainingAfter >= all.length) break;
    }

    const pendingRemaining = await getPendingCount();
    notify({ kind: 'flush_done', applied: appliedTotal, skipped: skippedTotal, errors: errorsTotal.length, pendingRemaining });
    return { applied: appliedTotal, skipped: skippedTotal, errors: errorsTotal, pendingRemaining };
  } finally {
    flushInFlight = false;
  }
};

/**
 * Pull server-side changes since the last successful pull.
 * Returns the server response; caller can merge it into local stores.
 */
export const pullChanges = async (collections = null) => {
  if (!isOnline()) return { collections: {}, offline: true };

  const since = await getLastPullAt();
  const url = new URL(`${API}/sync/pull`, window.location.href);
  if (since) url.searchParams.set('since', since);
  if (collections) url.searchParams.set('collections', collections.join(','));

  const response = await fetch(url.toString(), { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Pull failed: HTTP ${response.status}`);
  }
  const data = await response.json();
  if (data.server_time) {
    await setLastPullAt(data.server_time);
  }
  notify({ kind: 'pull_done', collections: Object.keys(data.collections || {}) });
  return data;
};

/**
 * Wire global online/offline events to drive auto-flush.
 * Call once from App.js at mount.
 */
export const startSyncEngine = () => {
  if (typeof window === 'undefined') return () => {};

  const onlineHandler = () => {
    notify({ kind: 'online' });
    flush().catch(() => {});
  };
  const offlineHandler = () => notify({ kind: 'offline' });

  window.addEventListener('online', onlineHandler);
  window.addEventListener('offline', offlineHandler);

  // Best-effort first flush
  if (isOnline()) {
    setTimeout(() => flush().catch(() => {}), 1500);
  }

  // Periodic flush every 60s while online
  const interval = setInterval(() => {
    if (isOnline()) flush().catch(() => {});
  }, 60000);

  return () => {
    window.removeEventListener('online', onlineHandler);
    window.removeEventListener('offline', offlineHandler);
    clearInterval(interval);
  };
};

export default {
  subscribeSync,
  isOnline,
  enqueueMutation,
  getPendingMutations,
  getPendingCount,
  flush,
  pullChanges,
  startSyncEngine,
  clearQueue,
};
